import {
  bindCollectionFilterTags,
  handleTagClick,
  updateActiveTagsFromURL
} from '@theme/collection-filters-bar-tags';
import {
  bindFilterSearchInputs,
  resetFilterSearchInScope
} from './collection-filters-search.js';
import { openDrawerFilterByParam, normalizeFilterParam } from '@theme/filter-drawer';

const initializedBars = new WeakSet();
const barRefreshers = new WeakMap();
const barCloseHandlers = new WeakMap();
const DESKTOP_FILTER_SCROLL_EDGE_TOLERANCE = 24;
const COLLECTION_FILTERS_MOBILE_MAX_WIDTH = 749;

function getRootForMobileFilterTrigger(trigger) {
  const directRoot = trigger.closest('.collection-filters-bar[data-section-id]');
  if (directRoot) return directRoot;

  const stickyLayer = trigger.closest('.collection-filters-bar__mobile-sticky-layer[data-section-id]');
  const sectionId = stickyLayer?.dataset.sectionId;
  if (!sectionId) return null;

  return document.querySelector(`.collection-filters-bar[data-section-id="${CSS.escape(sectionId)}"]`);
}

function openMobileFilterDrawerFromTrigger(trigger) {
  const root = getRootForMobileFilterTrigger(trigger);
  const drawer = document.getElementById('filters-drawer');
  const filterParam = normalizeFilterParam(trigger.getAttribute('data-mobile-filter-drawer-target') || '');

  if (!root || !drawer || !filterParam) return false;

  root.dataset.selectedMobileFilterParam = filterParam;
  drawer.dataset.selectedMobileFilterParam = filterParam;
  drawer.dataset.openFilterParam = filterParam;

  root.querySelectorAll('[data-mobile-filter-trigger]').forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
  });

  const sourceTrigger = root.querySelector(`[data-mobile-filter-trigger="${CSS.escape(filterParam)}"]`);
  if (sourceTrigger) {
    sourceTrigger.setAttribute('aria-expanded', 'true');
  }

  if (typeof drawer.showDialog === 'function') {
    drawer.showDialog();
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      openDrawerFilterByParam(drawer, filterParam);
    });
  });

  return true;
}

function bindMobileDrawerDocumentControlsOnce() {
  if (document.documentElement.dataset.cfbMobileDrawerControlsBound === 'true') return;
  document.documentElement.dataset.cfbMobileDrawerControlsBound = 'true';

  document.addEventListener('click', (event) => {
    if (window.innerWidth > COLLECTION_FILTERS_MOBILE_MAX_WIDTH) return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-mobile-filter-drawer-target]');
    if (!(trigger instanceof HTMLElement)) return;

    if (!openMobileFilterDrawerFromTrigger(trigger)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
}

function bindDocumentClickOnce() {
  if (document.documentElement.dataset.cfbDocumentClickBound === 'true') return;
  document.documentElement.dataset.cfbDocumentClickBound = 'true';

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.collection-filters-bar[data-section-id]').forEach((barRoot) => {
      const closeAllDropdowns = barCloseHandlers.get(barRoot);
      if (!closeAllDropdowns) return;

      const clickedInsideDropdown = event.target.closest('.cfb-dropdown');
      const clickedDropdownButton = event.target.closest('[data-dropdown-button]');
      const clickedSortingButton = event.target.closest('[data-sorting-button], [data-sorting-button-mobile]');
      const clickedFiltersButton = event.target.closest('[data-filters-button]');

      if (
        !barRoot.contains(event.target) ||
        (!clickedInsideDropdown && !clickedDropdownButton && !clickedSortingButton && !clickedFiltersButton)
      ) {
        closeAllDropdowns();
      }
    });
  });
}

class CollectionFiltersBarController {
  constructor(root, sectionId) {
    this.root = root;
    this.sectionId = sectionId;
    this.filtersDrawer = document.getElementById('filters-drawer');
    this.filtersDrawerDialog = this.filtersDrawer ? this.filtersDrawer.querySelector('dialog') : null;
    this.sortingDrawer = document.getElementById(`sorting-drawer-${sectionId}`);
    this.sortingDrawerDialog = this.sortingDrawer ? this.sortingDrawer.querySelector('dialog') : null;
    this.allDropdownPanels = Array.from(root.querySelectorAll('.cfb-dropdown[data-dropdown-panel], .cfb-dropdown[data-filters-dropdown], .cfb-dropdown[data-sorting-dropdown], .cfb-dropdown[data-sorting-dropdown-mobile]'));
    this.desktopFilterApplyTimer = 0;
  }

  init() {
    this.root.dataset.currentSortValue = this.getCurrentSortValue();
    this.setupMobileStickyMirror();
    this.resetMobileFilterScrollers();
    this.bindDesktopFilterScrollerControls();
    this.rebindFilterSearchInputs();
    this.bindDrawerInputSync();
    this.bindSortingControls();
    this.bindRootInput();
    this.bindRootChange();
    this.bindRootClick();
    this.bindDropdownScrollSync();
    this.bindDrawerClose();
    this.bindDrawerOpenState();
    this.bindDrawerOpenFilter();
    this.bindDrawerSelectionSync();
    this.bindResize();
    barCloseHandlers.set(this.root, this.closeAllDropdowns.bind(this));
    bindDocumentClickOnce();
    bindMobileDrawerDocumentControlsOnce();
    bindCollectionFilterTags(this.root);
    this.syncFilterPillButtons();
    this.bindMobileActiveFiltersLayout();
    this.registerRefresh();
    this.bindSyncEvents();
  }

  ensureMobileStickyLayer() {
    if (this.mobileStickyLayer) return this.mobileStickyLayer;

    const layer = document.createElement('div');
    layer.className = 'collection-filters-bar__mobile-sticky-layer';
    layer.dataset.sectionId = this.sectionId;
    layer.setAttribute('aria-hidden', 'true');
    layer.addEventListener('click', (event) => {
      this.handleMobileStickyMirrorClick(event);
    });
    document.body.appendChild(layer);
    this.mobileStickyLayer = layer;
    return layer;
  }

  handleMobileStickyMirrorClick(event) {
    const mobileBar = this.root.querySelector('.collection-filters-bar__mobile');
    if (!mobileBar) return;

    const mirrorControl = event.target.closest('[data-filters-button], [data-sorting-button-mobile], [data-mobile-filter-trigger]');
    if (!mirrorControl) return;

    event.preventDefault();

    let sourceControl = null;
    if (mirrorControl.matches('[data-filters-button]')) {
      sourceControl = mobileBar.querySelector('[data-filters-button]');
    } else if (mirrorControl.matches('[data-sorting-button-mobile]')) {
      sourceControl = mobileBar.querySelector('[data-sorting-button-mobile]');
    } else if (mirrorControl.dataset.mobileFilterTrigger) {
      sourceControl = mobileBar.querySelector(`[data-mobile-filter-trigger="${CSS.escape(mirrorControl.dataset.mobileFilterTrigger)}"]`);
    }

    sourceControl?.click();
  }

  syncMobileStickyMirror() {
    const mobileBar = this.root.querySelector('.collection-filters-bar__mobile');
    if (!mobileBar || window.innerWidth > COLLECTION_FILTERS_MOBILE_MAX_WIDTH) {
      if (this.mobileStickyLayer) {
        this.mobileStickyLayer.classList.remove('is-active');
        this.mobileStickyLayer.setAttribute('aria-hidden', 'true');
        this.mobileStickyLayer.replaceChildren();
      }
      return;
    }

    const layer = this.ensureMobileStickyLayer();
    const isStuck = mobileBar.classList.contains('collection-filters-bar__mobile--stuck');

    if (!isStuck) {
      layer.classList.remove('is-active');
      layer.setAttribute('aria-hidden', 'true');
      layer.replaceChildren();
      return;
    }

    let mirror = layer.querySelector('.collection-filters-bar__mobile--mirror');
    if (!mirror) {
      mirror = document.createElement('div');
      mirror.className = 'collection-filters-bar__mobile collection-filters-bar__mobile--mirror';
      layer.replaceChildren(mirror);
    }

    const sourceScroller = mobileBar.querySelector('.collection-filters-bar__mobile-scroller');
    const scrollerLeft = sourceScroller ? sourceScroller.scrollLeft : 0;
    mirror.innerHTML = mobileBar.innerHTML;

    const mirrorScroller = mirror.querySelector('.collection-filters-bar__mobile-scroller');
    if (mirrorScroller) {
      mirrorScroller.scrollLeft = scrollerLeft;
    }

    layer.classList.add('is-active');
    layer.setAttribute('aria-hidden', 'false');
  }

  setupMobileStickyMirror() {
    const mobileBar = this.root.querySelector('.collection-filters-bar__mobile');
    if (!mobileBar) return;

    const syncMirror = () => {
      this.syncMobileStickyMirror();
    };

    if (typeof ResizeObserver !== 'undefined') {
      const mirrorResizeObserver = new ResizeObserver(syncMirror);
      mirrorResizeObserver.observe(mobileBar);
    }

    if (this.root.dataset.mobileStickyMirrorBound === 'true') return;
    this.root.dataset.mobileStickyMirrorBound = 'true';

    document.addEventListener('cfb-mobile-filters-stuck-change', (event) => {
      const detail = event.detail || {};
      if (detail.filtersBar && !this.root.contains(detail.filtersBar)) return;
      syncMirror();
    });

    document.addEventListener('theme:mobile-sticky-change', syncMirror);
    window.addEventListener('resize', syncMirror);
  }

  alignMobileFilterScrollerStart(scroller) {
    if (!scroller) return;

    const apply = () => {
      scroller.scrollLeft = 0;
    };

    apply();
    requestAnimationFrame(apply);
  }

  resetMobileFilterScrollers() {
    this.root.querySelectorAll('.collection-filters-bar__mobile-scroller').forEach((scroller) => {
      this.alignMobileFilterScrollerStart(scroller);
    });
  }

  updateDesktopFilterScrollerControls() {
    const scroller = this.root.querySelector('[data-desktop-filter-scroller]');
    const prev = this.root.querySelector('[data-desktop-filter-scroll-prev]');
    const next = this.root.querySelector('[data-desktop-filter-scroll-next]');
    if (!scroller || !prev || !next) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const currentScroll = Math.min(maxScroll, Math.abs(scroller.scrollLeft));
    const hasOverflow = maxScroll > DESKTOP_FILTER_SCROLL_EDGE_TOLERANCE;
    const shell = scroller.closest('.collection-filters-bar__left-shell');
    const showPrev = hasOverflow && currentScroll > DESKTOP_FILTER_SCROLL_EDGE_TOLERANCE;
    const showNext = hasOverflow && maxScroll - currentScroll > DESKTOP_FILTER_SCROLL_EDGE_TOLERANCE;

    if (shell) {
      if (shell.classList.contains('has-horizontal-overflow') !== hasOverflow) {
        shell.classList.toggle('has-horizontal-overflow', hasOverflow);
      }
      if (shell.classList.contains('has-scroll-prev') !== showPrev) {
        shell.classList.toggle('has-scroll-prev', showPrev);
      }
      if (shell.classList.contains('has-scroll-next') !== showNext) {
        shell.classList.toggle('has-scroll-next', showNext);
      }
    }
    if (prev.hidden === showPrev) prev.hidden = !showPrev;
    if (next.hidden === showNext) next.hidden = !showNext;
  }

  bindDesktopFilterScrollerControls() {
    const scroller = this.root.querySelector('[data-desktop-filter-scroller]');
    const prev = this.root.querySelector('[data-desktop-filter-scroll-prev]');
    const next = this.root.querySelector('[data-desktop-filter-scroll-next]');
    if (!scroller || !prev || !next) return;

    const scrollToCalculatedFilterBoundary = (direction) => {
      const isRtl = window.getComputedStyle(scroller).direction === 'rtl';
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const currentScroll = Math.min(maxScroll, Math.abs(scroller.scrollLeft));
      const remainingToBoundary = direction > 0 ? maxScroll - currentScroll : currentScroll;

      if (remainingToBoundary <= DESKTOP_FILTER_SCROLL_EDGE_TOLERANCE) {
        this.updateDesktopFilterScrollerControls();
        return;
      }

      const pageStep = Math.max(220, Math.round(scroller.clientWidth * 0.72));
      const shortRemainder = Math.max(72, Math.round(pageStep * 0.28));
      let targetScroll = Math.max(0, Math.min(maxScroll, currentScroll + pageStep * direction));

      if (direction > 0 && maxScroll - targetScroll <= shortRemainder) targetScroll = maxScroll;
      if (direction < 0 && targetScroll <= shortRemainder) targetScroll = 0;

      const distance = (targetScroll - currentScroll) * (isRtl ? -1 : 1);
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      scroller.scrollBy({ left: Math.round(distance), behavior });
    };

    if (prev.dataset.filterScrollBound !== 'true') {
      prev.dataset.filterScrollBound = 'true';
      prev.addEventListener('click', () => scrollToCalculatedFilterBoundary(-1));
    }

    if (next.dataset.filterScrollBound !== 'true') {
      next.dataset.filterScrollBound = 'true';
      next.addEventListener('click', () => scrollToCalculatedFilterBoundary(1));
    }

    if (scroller.dataset.filterScrollControlsBound !== 'true') {
      scroller.dataset.filterScrollControlsBound = 'true';
      let updateFrame = 0;
      const scheduleScrollerUpdate = () => {
        if (updateFrame) return;
        updateFrame = window.requestAnimationFrame(() => {
          updateFrame = 0;
          this.updateDesktopFilterScrollerControls();
        });
      };

      scroller.addEventListener('scroll', scheduleScrollerUpdate, { passive: true });
      scroller.addEventListener('scrollend', scheduleScrollerUpdate, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this.desktopFilterResizeObserver?.disconnect();
        this.desktopFilterResizeObserver = new ResizeObserver(scheduleScrollerUpdate);
        this.desktopFilterResizeObserver.observe(scroller);
      }
    }

    window.requestAnimationFrame(() => this.updateDesktopFilterScrollerControls());
  }

  resetMobileFilterTriggers() {
    this.root.querySelectorAll('[data-mobile-filter-trigger]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  activateMobileFilterTrigger(triggerButton) {
    this.resetMobileFilterTriggers();
    if (!triggerButton) return;
    triggerButton.setAttribute('aria-expanded', 'true');
  }

  setSelectedMobileFilterParam(filterParamName) {
    if (!filterParamName) return;
    this.root.dataset.selectedMobileFilterParam = filterParamName;
    if (this.filtersDrawer) {
      this.filtersDrawer.dataset.selectedMobileFilterParam = filterParamName;
    }
  }

  getSelectedMobileFilterParam() {
    return this.root.dataset.selectedMobileFilterParam || this.filtersDrawer?.dataset.selectedMobileFilterParam || '';
  }

  syncOpenDrawerFilter(filterParamName) {
    if (!this.filtersDrawer || !filterParamName) return;

    const normalizedParam = normalizeFilterParam(filterParamName);
    const runSync = () => {
      openDrawerFilterByParam(this.filtersDrawer, normalizedParam);
    };
    const scheduleSync = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(runSync);
      });
    };

    if (this.filtersDrawerDialog?.open) {
      scheduleSync();
      return;
    }

    const onOpen = (event) => {
      if (event.target !== this.filtersDrawer) return;
      document.removeEventListener('dialog:open', onOpen);
      scheduleSync();
    };

    document.addEventListener('dialog:open', onOpen);
  }

  setDropdownState(dropdown, isOpen, triggerButton) {
    if (!dropdown) return;
    if (dropdown._closeTimer) {
      window.clearTimeout(dropdown._closeTimer);
      dropdown._closeTimer = null;
    }

    if (isOpen) {
      this.openDropdown(dropdown);
    } else {
      this.closeDropdown(dropdown);
    }

    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  openDropdown(dropdown) {
    dropdown.hidden = false;
    dropdown.style.display = 'block';
    dropdown.setAttribute('data-state', 'open');
    bindFilterSearchInputs(dropdown);
    this.syncDropdownScrollState(dropdown);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.syncDropdownScrollState(dropdown);
        dropdown.classList.add('is-open');
      });
    });
  }

  syncDropdownScrollState(dropdown) {
    if (!dropdown) return;

    dropdown.querySelectorAll('.cfb-dropdown__content--filters .facets__inputs-wrapper').forEach((wrapper) => {
      const maxHeight = Number.parseFloat(window.getComputedStyle(wrapper).maxHeight);
      const availableHeight = Number.isFinite(maxHeight) ? maxHeight : wrapper.clientHeight;
      const isScrollable = availableHeight > 0 && wrapper.scrollHeight > availableHeight + 1;
      wrapper.classList.toggle('is-scrollable', isScrollable);
    });
  }

  bindDropdownScrollSync() {
    if (this.root.dataset.cfbDropdownScrollBound === 'true') return;
    this.root.dataset.cfbDropdownScrollBound = 'true';

    const syncFromSearch = (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('[data-drawer-filter-search]')) return;
      const dropdown = event.target.closest('.cfb-dropdown');
      window.requestAnimationFrame(() => this.syncDropdownScrollState(dropdown));
    };

    this.root.addEventListener('input', syncFromSearch);
    this.root.addEventListener('search', syncFromSearch);
  }

  closeDropdown(dropdown) {
    dropdown.classList.remove('is-open');
    dropdown.setAttribute('data-state', 'closed');
    resetFilterSearchInScope(dropdown);
    this.resetMobileDropdownPosition(dropdown);
    this.resetPendingMobileSort(dropdown);
    dropdown._closeTimer = window.setTimeout(() => {
      dropdown.style.display = 'none';
      dropdown.hidden = true;
    }, 220);
  }

  closeAllDropdowns(exceptNode) {
    this.allDropdownPanels.forEach((node) => {
      if (node === exceptNode) return;
      const triggerButton = this.root.querySelector('[aria-controls="' + node.id + '"]');
      this.setDropdownState(node, false, triggerButton);
    });
  }

  resetMobileDropdownPosition(dropdown) {
    if (!dropdown) return;
    const filterContent = dropdown.querySelector('.cfb-dropdown__content--filters');
    dropdown.style.position = '';
    dropdown.style.top = '';
    dropdown.style.bottom = '';
    dropdown.style.left = '';
    dropdown.style.right = '';
    dropdown.style.width = '';
    dropdown.style.minWidth = '';
    dropdown.style.maxWidth = '';
    dropdown.style.maxHeight = '';
    dropdown.style.borderRadius = '';
    dropdown.style.zIndex = '';
    dropdown.style.transformOrigin = '';
    if (filterContent) filterContent.style.maxHeight = '';
  }

  positionMobileDropdown(dropdown, triggerButton) {
    if (!dropdown || !triggerButton || window.innerWidth > COLLECTION_FILTERS_MOBILE_MAX_WIDTH) return;

    const isFilterDropdown = dropdown.classList.contains('cfb-dropdown--mobile-filter');
    const isSortDropdown = dropdown.hasAttribute('data-sorting-dropdown-mobile');
    if (!isFilterDropdown && !isSortDropdown) return;

    const triggerRect = triggerButton.getBoundingClientRect();
    const dropdownWidth = Math.min(280, Math.max(240, window.innerWidth - 28));

    dropdown.style.position = 'fixed';
    dropdown.style.zIndex = '10005';
    dropdown.style.width = 'auto';
    dropdown.style.minWidth = '240px';
    dropdown.style.maxWidth = dropdownWidth + 'px';

    if (isSortDropdown) {
      dropdown.style.top = 'auto';
      dropdown.style.bottom = '0';
      dropdown.style.left = '0';
      dropdown.style.right = '0';
      dropdown.style.width = '100vw';
      dropdown.style.minWidth = '0';
      dropdown.style.maxWidth = 'none';
      dropdown.style.borderRadius = '20px 20px 0 0';
      return;
    }

    dropdown.style.top = Math.round(triggerRect.bottom + 8) + 'px';
    dropdown.style.left = '14px';
    dropdown.style.right = '14px';
    dropdown.style.maxWidth = 'none';
  }

  openDrawerForFilter(filterParamName, triggerButton) {
    if (!this.filtersDrawer || !filterParamName) return;

    const normalizedParam = normalizeFilterParam(filterParamName);
    if (!normalizedParam) return;

    this.setSelectedMobileFilterParam(normalizedParam);
    this.filtersDrawer.dataset.openFilterParam = normalizedParam;
    this.activateMobileFilterTrigger(triggerButton);

    if (typeof this.filtersDrawer.showDialog === 'function') {
      this.filtersDrawer.showDialog();
    }

    this.syncOpenDrawerFilter(normalizedParam);
  }

  syncMatchingInputs(sourceInput) {
    if (!sourceInput || !sourceInput.name) return;

    const selector = 'input[name="' + CSS.escape(sourceInput.name) + '"]';
    const matchingInputs = Array.from(document.querySelectorAll(selector)).filter((input) => {
      return input.type === sourceInput.type && input.value === sourceInput.value;
    });

    matchingInputs.forEach((input) => {
      if (input !== sourceInput) {
        input.checked = sourceInput.checked;
      }

      const option = input.closest('.cfb-brand-option');
      if (option) {
        option.classList.toggle('is-active', input.checked);
      }

      const stockLabel = input.closest('.cfb-stock-pill__label');
      if (stockLabel) {
        stockLabel.classList.toggle('is-active', input.checked);
      }
    });
  }

  rebindFilterSearchInputs() {
    bindFilterSearchInputs(this.filtersDrawer);
    bindFilterSearchInputs(this.root);
  }

  bindDrawerInputSync() {
    if (!this.filtersDrawer || this.filtersDrawer.dataset.drawerChangeSyncInitialized === 'true') return;

    this.filtersDrawer.dataset.drawerChangeSyncInitialized = 'true';
    this.filtersDrawer.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      const changedInput = event.target.closest('input[type="checkbox"]');
      if (changedInput) {
        this.syncMatchingInputs(changedInput);
        window.requestAnimationFrame(() => {
          this.syncFilterPillButtons();
          this.syncMobileStickyMirror();
        });
      }
    }, true);
  }

  getMainFacetsForm() {
    const form = document.getElementById('FacetFiltersForm--' + this.sectionId);
    if (form) {
      const facetsForm = form.closest('facets-form-component');
      if (facetsForm) return facetsForm;
    }
    return this.root.querySelector('facets-form-component');
  }

  getCurrentSortValue() {
    const checkedInput = this.root.querySelector('[data-sorting-option]:checked, [data-sorting-option-mobile]:checked');
    if (checkedInput instanceof HTMLInputElement && checkedInput.value) {
      return checkedInput.value;
    }

    return this.root.querySelector('[data-sorting-option], [data-sorting-option-mobile]')?.value || 'manual';
  }

  applySortSelection(selectedInput, optionLabelNode, dropdown, triggerButton) {
    if (!(selectedInput instanceof HTMLInputElement)) return;

    const sortValue = selectedInput.value;
    const optionLabel = optionLabelNode || selectedInput.closest('label')?.querySelector('.cfb-dropdown__label');
    this.syncSortInputs(sortValue, optionLabel);
    this.setDropdownState(dropdown, false, triggerButton);
    this.applySortURL(sortValue, optionLabel);
  }

  syncSortInputs(sortValue, optionLabel) {
    this.root.dataset.currentSortValue = sortValue || 'manual';

    this.root.querySelectorAll('input[name="sort_by"]').forEach((radio) => {
      if (radio instanceof HTMLInputElement) {
        radio.checked = radio.value === sortValue;
      }
    });

    if (!optionLabel) return;

    this.root.querySelectorAll('[data-sorting-label]').forEach((labelNode) => {
      labelNode.textContent = optionLabel.textContent;
    });
  }

  applySortURL(sortValue, optionLabel) {
    const url = new URL(window.location.href);
    const currentParams = new URLSearchParams(url.search);

    if (!sortValue || sortValue === 'manual') {
      currentParams.delete('sort_by');
    } else {
      currentParams.set('sort_by', sortValue);
    }

    currentParams.delete('page');
    url.search = currentParams.toString();

    const facetsForm = this.getMainFacetsForm();
    if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url.toString(), { preserveSort: false });
      return;
    }

    window.location.href = url.toString();
  }

  resetPendingMobileSort(container) {
    if (!container) return;
    if (!container.querySelector('[data-sorting-option-mobile]')) return;

    const currentSortValue = this.root.dataset.currentSortValue || this.getCurrentSortValue();
    container.querySelectorAll('[data-sorting-option-mobile]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === currentSortValue;
      }
    });
  }

  bindSortingDropdown(button, dropdown, optionSelector) {
    if (!button || !dropdown) return;
    if (button.dataset.sortingBound === 'true') return;
    button.dataset.sortingBound = 'true';

    button.addEventListener('click', (event) => {
      const isDesktopSorting = button.matches('[data-sorting-button]');
      if (isDesktopSorting && window.innerWidth <= COLLECTION_FILTERS_MOBILE_MAX_WIDTH) return;

      event.preventDefault();
      event.stopPropagation();
      const isOpen = dropdown.classList.contains('is-open');
      this.closeAllDropdowns(isOpen ? null : dropdown);
      if (dropdown.classList.contains('cfb-dropdown--mobile') || dropdown.classList.contains('cfb-dropdown--mobile-filter')) {
        if (dropdown.hasAttribute('data-sorting-dropdown-mobile')) {
          this.resetPendingMobileSort(dropdown);
        }
        this.positionMobileDropdown(dropdown, button);
      }
      this.setDropdownState(dropdown, !isOpen, button);
    });

    dropdown.querySelectorAll(optionSelector).forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        if (dropdown.hasAttribute('data-sorting-dropdown-mobile')) return;
        this.applySortSelection(input, input.closest('label')?.querySelector('.cfb-dropdown__label'), dropdown, button);
      });
    });
  }

  bindSortingControls() {
    this.root.querySelectorAll('[data-sorting-button]').forEach((button) => {
      const sortingContainer = button.closest('.cfb-sorting');
      const dropdown = sortingContainer ? sortingContainer.querySelector('[data-sorting-dropdown]') : null;
      this.bindSortingDropdown(button, dropdown, '[data-sorting-option]');
    });

    const sortingButtonMobile = this.root.querySelector('[data-sorting-button-mobile]');
    if (!sortingButtonMobile) return;
    const sortingDrawer = this.sortingDrawer;
    if (!sortingDrawer) return;

    if (!sortingButtonMobile.dataset.mobileSortDrawerBound) {
      sortingButtonMobile.dataset.mobileSortDrawerBound = 'true';
      sortingButtonMobile.addEventListener('click', () => {
        this.resetPendingMobileSort(sortingDrawer);
      });
    }

    if (!sortingDrawer.dataset.mobileSortDrawerBound) {
      sortingDrawer.dataset.mobileSortDrawerBound = 'true';

      document.addEventListener('dialog:open', (event) => {
        if (event.target !== sortingDrawer) return;
        this.resetPendingMobileSort(sortingDrawer);
      });

      document.addEventListener('dialog:close', (event) => {
        if (event.target !== sortingDrawer) return;
        this.resetPendingMobileSort(sortingDrawer);
      });

      const applyButton = sortingDrawer.querySelector('[data-sorting-apply-mobile]');
      if (applyButton) {
        applyButton.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          const selectedInput = sortingDrawer.querySelector('[data-sorting-option-mobile]:checked');
          if (!selectedInput) {
            if (typeof sortingDrawer.closeDialog === 'function') {
              await sortingDrawer.closeDialog();
            }
            return;
          }

          this.syncSortInputs(
            selectedInput.value,
            selectedInput.closest('label')?.querySelector('.cfb-dropdown__label')
          );

          if (typeof sortingDrawer.closeDialog === 'function') {
            await sortingDrawer.closeDialog();
          }

          this.applySortURL(
            selectedInput.value,
            selectedInput.closest('label')?.querySelector('.cfb-dropdown__label')
          );
        });
      }
    }
  }

  getFilterPanelActiveCount(panel) {
    if (!panel) return 0;

    const statusBubble = panel.querySelector('facet-status-component .cfb-counter, facet-status-component [ref="facetStatus"]');
    if (statusBubble) {
      const parsed = parseInt(statusBubble.textContent.trim(), 10);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
      if (statusBubble.textContent.trim() !== '') return 1;
    }

    const priceFacet = panel.querySelector('.price-facet');
    if (!priceFacet) return panel.querySelectorAll('input[type="checkbox"]:checked').length;

    const minInput = priceFacet.querySelector('input[name*="filter.v.price.gte"]');
    const maxInput = priceFacet.querySelector('input[name*="filter.v.price.lte"]');
    const hasMin = minInput instanceof HTMLInputElement && minInput.value.trim() !== '';
    const hasMax = maxInput instanceof HTMLInputElement && maxInput.value.trim() !== '';
    return hasMin || hasMax ? 1 : 0;
  }

  updateFilterPillButton(button) {
    const param = button.getAttribute('data-filter-param');
    if (!param) return;

    const panel = this.getFilterPanel(param);
    const count = this.getFilterPanelActiveCount(panel);
    let countEl = button.querySelector('[data-filter-pill-count]');

    button.classList.toggle('is-active', count > 0);
    button.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');

    if (count <= 0) {
      if (countEl) countEl.remove();
      return;
    }

    if (!countEl) {
      const labelEl = button.querySelector('[data-filter-pill-label]');
      if (!labelEl) return;

      countEl = document.createElement('span');
      countEl.className = 'cfb-tag__count';
      countEl.setAttribute('data-filter-pill-count', '');
      labelEl.insertAdjacentElement('afterend', countEl);
    }

    countEl.textContent = ' (' + count + ')';
  }

  getFilterPanel(param) {
    const desktopPanel = this.root.querySelector('[data-dropdown-panel="desktop-' + param + '"]');
    if (desktopPanel || !this.filtersDrawer) return desktopPanel;
    return this.filtersDrawer.querySelector('accordion-custom[data-filter-param-name="' + CSS.escape(param) + '"]');
  }

  syncFilterPillButtons() {
    this.root.querySelectorAll('[data-filter-param]').forEach((button) => {
      this.updateFilterPillButton(button);
    });
  }

  bindRootChange() {
    this.root.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      this.handleOptionInputChange(event.target);
      this.scheduleDesktopDropdownApply(event.target);

      if (event.target.closest('facet-inputs-component, .facets__panel-content, .price-facet')) {
        window.requestAnimationFrame(() => {
          this.syncFilterPillButtons();
        });
      }
    }, true);
  }

  bindRootInput() {
    this.root.addEventListener('input', (event) => {
      if (!(event.target instanceof Element)) return;
      this.scheduleDesktopDropdownApply(event.target);
    }, true);
  }

  scheduleDesktopDropdownApply(target) {
    if (target.closest('#filters-drawer')) return;
    if (!target.closest('.price-facet')) return;

    window.clearTimeout(this.desktopFilterApplyTimer);
    this.desktopFilterApplyTimer = window.setTimeout(() => {
      const url = this.buildDesktopFiltersURL();
      if (!url) return;

      const facetsForm = this.getMainFacetsForm();
      if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') {
        window.location.assign(url.toString());
        return;
      }

      try {
        const update = facetsForm.updateFiltersByURL(url.toString());

        if (update && typeof update.catch === 'function') {
          update.catch(() => {
            window.location.assign(url.toString());
          });
        }
      } catch {
        window.location.assign(url.toString());
      }
    }, 50);
  }

  buildDesktopFiltersURL() {
    const form = document.getElementById('FacetFiltersForm--' + this.sectionId);
    if (!(form instanceof HTMLFormElement)) return null;

    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    const currentURL = new URL(window.location.href);
    const parameters = new URLSearchParams();
    const controls = Array.from(this.root.querySelectorAll('input[name], select[name], textarea[name]'));

    controls.forEach((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) return;
      if (control.disabled || !control.name) return;

      if (control instanceof HTMLInputElement) {
        const type = control.type;
        if (type === 'range') return;
        if ((type === 'checkbox' || type === 'radio') && !control.checked) return;
        if (type === 'button' || type === 'submit' || type === 'reset') return;
      }

      if (control instanceof HTMLSelectElement && control.multiple) {
        Array.from(control.selectedOptions).forEach((option) => {
          if (option.value !== '') parameters.append(control.name, option.value);
        });
        return;
      }

      if (control.value !== '') {
        parameters.append(control.name, control.value);
      }
    });

    if (!parameters.has('sort_by') && currentURL.searchParams.has('sort_by')) {
      parameters.set('sort_by', currentURL.searchParams.get('sort_by'));
    }

    if (parameters.get('filter.v.price.gte') === '') parameters.delete('filter.v.price.gte');
    if (parameters.get('filter.v.price.lte') === '') parameters.delete('filter.v.price.lte');

    parameters.delete('page');
    if (parameters.get('sort_by') === 'manual') parameters.delete('sort_by');

    url.search = parameters.toString();
    return url;
  }

  handleOptionInputChange(target) {
    const checkbox = target.closest('.cfb-brand-option__input');
    if (checkbox) {
      this.syncMatchingInputs(checkbox);
      const option = checkbox.closest('.cfb-brand-option');
      if (option) {
        option.classList.toggle('is-active', checkbox.checked);
      }
    }

    const stockInput = target.closest('.cfb-stock-pill__input');
    if (!stockInput) return;

    this.syncMatchingInputs(stockInput);
    const stockLabel = stockInput.closest('.cfb-stock-pill__label');
    if (stockLabel) {
      stockLabel.classList.toggle('is-active', stockInput.checked);
    }
  }

  bindRootClick() {
    this.root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (this.handleClearFiltersClick(event)) return;
      if (this.handleFiltersButtonClick(event)) return;
      if (this.handleDropdownButtonClick(event)) return;
      if (this.handleMobileFilterButtonClick(event)) return;

      const tagLink = event.target.closest('.cfb-tag[data-tag-handle]');
      if (tagLink && this.root.contains(tagLink)) {
        handleTagClick(event);
      }
    });
  }

  handleClearFiltersClick(event) {
    const clearButton = event.target.closest('.collection-filters-bar__clear-link');
    if (!clearButton || !this.root.contains(clearButton)) return false;

    const removeComponent = clearButton.closest('facet-remove-component');
    const url = removeComponent?.dataset.url || clearButton.getAttribute('href');
    if (!url) return false;

    event.preventDefault();
    event.stopPropagation();

    const facetsForm = this.getMainFacetsForm();
    if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') {
      window.location.assign(url);
      return true;
    }

    try {
      const update = facetsForm.updateFiltersByURL(url);

      if (update && typeof update.catch === 'function') {
        update.catch(() => {
          window.location.assign(url);
        });
      }
    } catch {
      window.location.assign(url);
    }

    return true;
  }

  handleFiltersButtonClick(event) {
    const filtersButton = event.target.closest('[data-filters-button]');
    if (!filtersButton || !this.root.contains(filtersButton)) return false;
    if (window.innerWidth <= COLLECTION_FILTERS_MOBILE_MAX_WIDTH) {
      event.preventDefault();
      event.stopPropagation();
      const selectedFilterParam = this.getSelectedMobileFilterParam();
      if (selectedFilterParam) {
        const selectedTrigger = this.root.querySelector('[data-mobile-filter-trigger="' + CSS.escape(selectedFilterParam) + '"]');
        this.openDrawerForFilter(selectedFilterParam, selectedTrigger);
      } else if (typeof this.filtersDrawer?.showDialog === 'function') {
        this.filtersDrawer.showDialog();
      }
      return true;
    }

    const trigger = filtersButton.closest('.cfb-dropdown-trigger');
    const dropdown = trigger ? trigger.querySelector('[data-filters-dropdown]') : null;
    if (!dropdown) return true;

    event.preventDefault();
    event.stopPropagation();
    const isOpen = dropdown.classList.contains('is-open');
    this.closeAllDropdowns(isOpen ? null : dropdown);
    this.setDropdownState(dropdown, !isOpen, filtersButton);
    return true;
  }

  handleDropdownButtonClick(event) {
    const dropdownButton = event.target.closest('[data-dropdown-button]');
    if (!dropdownButton || !this.root.contains(dropdownButton)) return false;

    const target = dropdownButton.getAttribute('data-dropdown-target');
    const dropdown = target ? this.root.querySelector('[data-dropdown-panel="' + target + '"]') : null;
    if (!dropdown) return true;

    event.preventDefault();
    event.stopPropagation();
    const isOpen = dropdown.classList.contains('is-open');
    this.closeAllDropdowns(isOpen ? null : dropdown);
    this.positionMobileDropdown(dropdown, dropdownButton);
    this.setDropdownState(dropdown, !isOpen, dropdownButton);
    return true;
  }

  handleMobileFilterButtonClick(event) {
    const mobileFilterButton = event.target.closest('[data-mobile-filter-drawer-target]');
    if (!mobileFilterButton || !this.root.contains(mobileFilterButton)) return false;
    if (window.innerWidth > COLLECTION_FILTERS_MOBILE_MAX_WIDTH) return true;

    event.preventDefault();
    event.stopPropagation();
    this.openDrawerForFilter(mobileFilterButton.getAttribute('data-mobile-filter-drawer-target'), mobileFilterButton);
    return true;
  }

  bindDrawerClose() {
    if (!this.filtersDrawerDialog) return;

    this.filtersDrawerDialog.addEventListener('close', () => {
      document.documentElement.classList.remove('filters-drawer-open');
      this.resetMobileFilterTriggers();
      if (this.filtersDrawer) {
        delete this.filtersDrawer.dataset.openFilterParam;
      }
      resetFilterSearchInScope(this.filtersDrawer);
    });
  }

  bindDrawerOpenState() {
    if (!this.filtersDrawer || this.filtersDrawer.dataset.drawerOpenStateBound === 'true') return;
    this.filtersDrawer.dataset.drawerOpenStateBound = 'true';

    document.addEventListener('dialog:open', (event) => {
      if (event.target !== this.filtersDrawer) return;
      document.documentElement.classList.add('filters-drawer-open');
    });

    document.addEventListener('dialog:close', (event) => {
      if (event.target !== this.filtersDrawer) return;
      document.documentElement.classList.remove('filters-drawer-open');
    });
  }

  bindDrawerOpenFilter() {
    if (!this.filtersDrawer || this.filtersDrawer.dataset.drawerOpenFilterBound === 'true') return;
    this.filtersDrawer.dataset.drawerOpenFilterBound = 'true';

    this.filtersDrawer.addEventListener('dialog:open', () => {
      const filterParam = this.filtersDrawer.dataset.openFilterParam;
      if (!filterParam) return;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          openDrawerFilterByParam(this.filtersDrawer, filterParam);
        });
      });
    });
  }

  bindDrawerSelectionSync() {
    if (!this.filtersDrawer || this.filtersDrawer.dataset.drawerSelectionSyncBound === 'true') return;
    this.filtersDrawer.dataset.drawerSelectionSyncBound = 'true';

    this.filtersDrawer.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;

      const accordion = details.closest('accordion-custom[data-filter-param-name]');
      if (!(accordion instanceof HTMLElement)) return;

      const filterParamName = accordion.getAttribute('data-filter-param-name');
      if (!filterParamName) return;

      this.setSelectedMobileFilterParam(filterParamName);
      const selectedTrigger = this.root.querySelector('[data-mobile-filter-trigger="' + CSS.escape(filterParamName) + '"]');
      this.activateMobileFilterTrigger(selectedTrigger);
    }, true);
  }

  bindResize() {
    if (this.root.dataset.cfbResizeBound === 'true') return;
    this.root.dataset.cfbResizeBound = 'true';

    window.addEventListener('resize', () => {
      this.root.querySelectorAll('.cfb-dropdown--mobile-filter').forEach((dropdown) => {
        if (dropdown.hidden) return;
        const triggerButton = this.root.querySelector('[aria-controls="' + dropdown.id + '"]');
        this.positionMobileDropdown(dropdown, triggerButton);
      });
      this.root.querySelectorAll('.cfb-dropdown:not([hidden])').forEach((dropdown) => {
        this.syncDropdownScrollState(dropdown);
      });
      this.syncMobileActiveFiltersLayout();
    });
  }

  bindMobileActiveFiltersLayout() {
    const mobile = this.root.querySelector('.collection-filters-bar__mobile');
    if (!mobile || mobile.dataset.activeFiltersLayoutBound === 'true') return;
    mobile.dataset.activeFiltersLayoutBound = 'true';

    const runSync = () => {
      this.syncMobileActiveFiltersLayout();
    };

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        runSync();
      });
      observer.observe(mobile);
    }

    runSync();
  }

  syncMobileActiveFiltersLayout() {
    const mobile = this.root.querySelector('.collection-filters-bar__mobile');
    if (!mobile || window.innerWidth > COLLECTION_FILTERS_MOBILE_MAX_WIDTH) return;

    const row = mobile.querySelector('[data-active-filters-row]');
    if (!row) return;

    const pills = row.querySelector('.collection-filters-bar__active-pills');
    const clearLink = row.querySelector('.collection-filters-bar__clear-link');
    if (!pills || !clearLink) {
      row.classList.remove('collection-filters-bar__active-row--clear-first');
      return;
    }

    row.classList.remove('collection-filters-bar__active-row--clear-first');

    const rowStyles = getComputedStyle(row);
    const gap = parseFloat(rowStyles.columnGap || rowStyles.gap) || 10;
    const rowWidth = row.clientWidth;
    const pillsWidth = pills.scrollWidth;
    const clearWidth = clearLink.offsetWidth;
    const fitsOneLine = pillsWidth + clearWidth + gap <= rowWidth + 1;

    if (!fitsOneLine) {
      row.classList.add('collection-filters-bar__active-row--clear-first');
    }
  }

  registerRefresh() {
    barRefreshers.set(this.root, () => {
      this.rebindFilterSearchInputs();
      this.bindDesktopFilterScrollerControls();
      updateActiveTagsFromURL();
      this.syncFilterPillButtons();
      this.syncMobileActiveFiltersLayout();
      this.resetMobileFilterScrollers();
      this.syncMobileStickyMirror();
    });
  }

  bindSyncEvents() {
    if (this.root.dataset.cfbSyncBound === 'true') return;
    this.root.dataset.cfbSyncBound = 'true';

    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.refresh();
      }
    });

    document.addEventListener('filter:update', () => {
      this.refresh();
    });
  }

  refresh() {
    const refresh = barRefreshers.get(this.root);
    if (refresh) {
      window.requestAnimationFrame(refresh);
    }
  }
}

export function initCollectionFiltersBar(root) {
  if (!root || initializedBars.has(root)) return;
  initializedBars.add(root);

  const sectionId = root.dataset.sectionId;
  if (!sectionId) return;

  new CollectionFiltersBarController(root, sectionId).init();
}

function bootCollectionFiltersBars() {
  document.querySelectorAll('.collection-filters-bar[data-section-id]').forEach((root) => {
    initCollectionFiltersBar(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootCollectionFiltersBars);
} else {
  bootCollectionFiltersBars();
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event.detail?.sectionId;
  if (!sectionId) return;
  const root = document.querySelector(`.collection-filters-bar[data-section-id="${CSS.escape(String(sectionId))}"]`);
  if (!root) return;

  const refresh = barRefreshers.get(root);
  if (refresh) {
    window.requestAnimationFrame(refresh);
    return;
  }

  initCollectionFiltersBar(root);
});
